import { CommonModule } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { AlertModule } from "ngx-bootstrap/alert";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { environment } from "../../../environments/environment";
import { LLMComponent } from "../../home/vcloudsmart/llm/llm.component";
import { VFIconComponent } from "../../shared/component/vficon/vficon.component";
import { SignInEvent, UserPermissions } from "../../shared/models/tenancies";
import { PrettifyAttributePipe } from "../../shared/pipes/prettify-attribute.pipe";
import { AlertsService } from "../../shared/services/alerts.service";
import { AuthenticationService } from "../../shared/services/authentication.service";
import { AuthorisationService } from "../../shared/services/authorisation.service";
import { EventsService } from "../../shared/services/events.service";
import { LoggerService } from "../../shared/services/logger.service";
import { TenantService } from "../../shared/services/tenant.service";
import { VCloudSmartApiService } from "../../shared/services/v-cloud-smart-api.service";
import { FeatureTogglesService } from "../../shared/services/feature-toggles.service";
import { CurrencyStateService } from "../../shared/services/currency-state.service";
import { CSP } from "../../shared/models/csp-api";
import { UserNotificationDropdownComponent } from "../../home/notifications/user-notification-dropdown/user-notification-dropdown.component";
import { VcloudCostTrackerService } from "../../shared/services/vcloud-cost-tracker.service";
interface NavBarItem {
  tooltip: string;
  href: string;
  innerHTML: string;
  isInvisible?: boolean;
  isDisabled?: boolean;
}

@Component({
  imports: [
    CommonModule,
    RouterModule,
    VFIconComponent,
    TooltipModule,
    FontAwesomeModule,
    AlertModule,
    FormsModule,
    PrettifyAttributePipe,
    LLMComponent,
    UserNotificationDropdownComponent,
  ],
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
})
export class HeaderComponent implements OnInit {
  eventsService = inject(EventsService);
  authNService = inject(AuthenticationService);
  authZservice = inject(AuthorisationService);
  alerts = inject(AlertsService);
  vcs = inject(VCloudSmartApiService);
  private router = inject(Router);
  private logger = inject(LoggerService);
  public costTracker = inject(VcloudCostTrackerService);
  tenant = inject(TenantService);
  featureToggles = inject(FeatureTogglesService);
  private currencyState = inject(CurrencyStateService);

  protected readonly environment = environment;
  public Object = Object;
  public faBars = faBars;
  public startTime: string = "";
  public endTime: string = "";
  public defaultDays: number = 90;
  public navBarItems: NavBarItem[] = [];
  public readonly window = window;
  public isNotificationManagerEnabled: boolean = false;

  public lastSignIn?: SignInEvent;
  public user: UserPermissions = { user: {}, permissions: {} };
  public isLLMPage: boolean = false;
  public popUpAIActivated: boolean = false;
  public get allowedTenants(): string[] {
    return this.authNService.allowedTenants;
  }
  public get isETLErrorsEnabled(): boolean {
    if (Object(this.user.user.groups).hasOwnProperty("Anomaly-Detection")) {
      return this.user.user.groups?.["Anomaly-Detection"]?.includes("vcs-fls-team") ?? false;
    }
    return false;
  }

  public get currentTenant(): string {
    return this.authNService.tenant;
  }

  public set currentTenant(t: string) {
    this.authNService.tenant = t;
  }

  public get email(): string {
    return this.authNService.email || "";
  }

  public get pathname(): string {
    return location.pathname;
  }

  public get currentCsp(): CSP {
    if (this.pathname.includes("/gcp")) {
      return "gcp";
    }
    if (this.pathname.includes("/azure")) {
      return "azure";
    }
    if (this.pathname.includes("/oci")) {
      return "oci";
    }
    if (this.pathname.includes("/drcc")) {
      return "drcc";
    }
    return "aws";
  }

  public get currentCurrency() {
    return this.currencyState.getCurrency(this.currentCsp);
  }

  public set showAI(v: boolean) {
    this.eventsService.aiPopUpVisible.next(v);
  }

  public get showAI(): boolean {
    return this.eventsService.aiPopUpVisible.value;
  }

  ngOnInit(): void {
    if (this.authNService.isLoggedIn()) {
      this.authZservice.getUserPermissions({ attributes: ["sign_in_events"] }).subscribe((user) => {
        const {
          user: { sign_in_events },
        } = user;
        this.lastSignIn = (sign_in_events || [])[1];
      });
      this.authZservice
        .getUserPermissions({
          attributes: ["permissions", "groups", "tenancies", "allowed_tenants", "current_tenant"],
        })
        .subscribe((user) => {
          const {
            user: { current_tenant },
          } = user;
          this.user = user;
          this.isNotificationManagerEnabled = this.featureToggles.isFeatureEnabled(this.user, "notiification_manager");
          if (current_tenant && this.currentTenant !== "vodacom_za" && !this.router.url.includes("gcp")) {
            this.currentTenant = current_tenant;
          }
          this.navBarItems = this.getNavBarItems(current_tenant || this.currentTenant || "vodafone");
        });
    }
    this.endTime = new Date().toISOString().split("T")[0];
    this.startTime = this.addDays(new Date(), -this.defaultDays).toISOString().split("T")[0];
    this.eventsService.aiPopUpVisible.subscribe((v) => {
      if (v) {
        this.popUpAIActivated = true;
      }
    });
  }

  private addDays(date: number | string | Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  public logout(): void {
    localStorage.clear();
    sessionStorage.clear();
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    this.eventsService.startLoading("logout");
    this.logger.info("signing out");
    this.authNService.logout();
  }

  public switchTenant(newTenant: string): void {
    if (newTenant !== this.currentTenant) {
      this.authNService.tenant = newTenant;
      sessionStorage.removeItem("expensiveQueries");
      localStorage.removeItem("links");
      localStorage.removeItem("responses");
      localStorage.removeItem("exampleQuestions");
      for (const key of Object.keys(localStorage).filter((k) => {
        return k.startsWith("previousFilters/");
      })) {
        localStorage.removeItem(key);
      }
      for (const key of Object.keys(sessionStorage).filter((k) => {
        return k.startsWith("response-") || k.startsWith("cache-");
      })) {
        sessionStorage.removeItem(key);
      }
      window.location.reload();
    }
  }

  private getNavBarItems(tenant: string): NavBarItem[] {
    return [
      {
        tooltip: "View information about PCS on Confluence",
        href: "https://confluence.sp.vodafone.com/spaces/MCCH/pages/767863008/Public+Cloud+Services",
        innerHTML: "About",
        isDisabled: tenant !== "vodafone",
      },
      {
        tooltip: "View documentation about the PCS Portal on Confluence",
        href: "https://confluence.sp.vodafone.com/spaces/MCCH/pages/808244459/VCloudSmart+User+Guide+v2.0",
        innerHTML: "FAQ",
        isDisabled: tenant !== "vodafone",
      },
      {
        tooltip: "Request a new feature in the PCS Portal in the Service Catalogue",
        href: "https://servicecatalogue.vodafone.com/catalogue?id=sc_cat_item&sys_id=53b419e333c871d03f7343bfcd5c7b7a",
        innerHTML: "Feature Request",
        isDisabled: tenant !== "vodafone",
      },
      // {
      //   tooltip: "Report a problem in the PCS Portal in the Service Catalogue",
      //   href: "https://servicecatalogue.vodafone.com/catalogue/?id=sc_cat_item&sys_id=012175c887d05550997b8776cebb351f&sysparm_category=3350e5e21be53740e52e0e9c5e4bcbe0&catalog_id=-1",
      //   innerHTML: "Bug Report",
      //   isDisabled: tenant !== "vodafone",
      // },
      // {
      //   tooltip:
      //     "Service requests can be used to get access to additional projects or accounts as well as for any other issues such as missing values in drop-downs (Service Catalogue).",
      //   href: "https://servicecatalogue.vodafone.com/catalogue/?id=sc_cat_item&sys_id=42fe382bdbb1f740ef48b5ca6896198d&sysparm_category=c364b7c1db6a84d8324a19340596196a&catalog_id=-1",
      //   innerHTML: "Service Request",
      //   isDisabled: tenant !== "vodafone",
      // },
    ];
  }

  protected readonly localStorage = localStorage;
  public isExpanded: boolean = false;
  protected readonly location = location;
}
